import { ScopeConfiguration, ScopeVersion, StatusType } from '@app/core/model/scope-version'
import {
  ScopeOverviewLoadingState,
  ScopeOverviewState,
} from '@app/features/scope-overview/model/scope-overview.state.model'
import { State, getScopeOverviewState } from '@app/features/scope-overview/store/reducers/index'

import { ThirdPartyCost } from '@app/core/model/third-party-cost.model'
import { KeyValue } from '@app/core/types/key-value.type'
import { Activities } from '@app/features/scope-overview/model/activity.model'
import { FeeItemInstance } from '@app/features/scope-overview/model/fee-item.model'
import { Money } from '@app/features/scope-overview/model/money.model'
import { RatecardVersion } from '@app/features/scope-overview/model/ratecard-version.model'
import { Scenario } from '@app/features/scope-overview/model/scenario.model'
import { ScopeRetainedHoursStats } from '@app/features/scope-overview/model/scope-retained-hours-stats.model'
import { SowRetainedHoursStats } from '@app/features/scope-overview/model/sow-retained-hours-stats.model'
import { SowRetainedTeamStats } from '@app/features/scope-overview/model/sow-retained-team-stats.model'
import { Deliverable } from '@app/features/scoping/models/deliverable.model'
import { OfficeLocation } from '@core/model/office-location'
import { SecondParty } from '@core/model/second-party.model'
import { createSelector } from '@ngrx/store'
import { plainToInstance } from 'class-transformer'
import { ScopePagination } from '../../model/scope-pagination.model'
import { ScopeSection } from '../../model/scope-section'

const getFeatureState = createSelector(getScopeOverviewState, (state: State): ScopeOverviewState => state.scopeOverview)

/** Loading State */
const getLoadingState = createSelector(
  getFeatureState,
  (state: ScopeOverviewState): ScopeOverviewLoadingState => state.loading
)
const selectCurrentScopeLoading = createSelector(
  getLoadingState,
  (loadingState: ScopeOverviewLoadingState): boolean => loadingState?.scope
)
const selectCurrentScopeValuesLoading = createSelector(
  getLoadingState,
  (loadingState: ScopeOverviewLoadingState): boolean => loadingState?.scopeValues
)
const selectUpdateScopeDetailsLoading = createSelector(
  getLoadingState,
  (loadingState: ScopeOverviewLoadingState): boolean => loadingState?.updateScopeDetails
)
const selectAllTasksLoading = createSelector(
  getLoadingState,
  (loadingState: ScopeOverviewLoadingState): boolean => loadingState?.allTasks
)
const selectAddScopeDeliverableLoading = createSelector(
  getLoadingState,
  (loadingState: ScopeOverviewLoadingState): boolean => loadingState?.addScopeDeliverable
)
const selectAddScopeSectionLoading = createSelector(
  getLoadingState,
  (loadingState: ScopeOverviewLoadingState): boolean => loadingState?.addScopeSection
)
const selectDeliverableLoading = (sectionId: number, deliverableId: number) =>
  createSelector(
    getLoadingState,
    (loadingState: ScopeOverviewLoadingState): boolean =>
      !loadingState.section[sectionId] && loadingState.deliverable[deliverableId]
  )
const selectSectionLoading = (sectionId: number) =>
  createSelector(getLoadingState, (loadingState: ScopeOverviewLoadingState): boolean => loadingState.section[sectionId])

/** Loaded State */
const getLoadedState = createSelector(
  getFeatureState,
  (state: ScopeOverviewState): ScopeOverviewLoadingState => state.loaded
)
const selectCurrentScopeLoaded = createSelector(
  getLoadedState,
  (loadedState: ScopeOverviewLoadingState): boolean => loadedState.scope
)
const selectUpdateScopeDetailsLoaded = createSelector(
  getLoadedState,
  (loadedState: ScopeOverviewLoadingState): boolean => loadedState.updateScopeDetails
)
const selectLibraryTemplateLoaded = createSelector(
  getLoadedState,
  (loadedState: ScopeOverviewLoadingState): boolean => loadedState.libraryTemplate
)
const selectUpdateDeliverableLoaded = createSelector(
  getLoadedState,
  (loadedState: ScopeOverviewLoadingState): boolean => loadedState.updateDeliverable
)
const selectAllFeesLoaded = createSelector(
  getLoadedState,
  (loadedState: ScopeOverviewLoadingState): boolean => loadedState.allFees
)
const selectAllThirdPartyCostsLoaded = createSelector(
  getLoadedState,
  (loadedState: ScopeOverviewLoadingState): boolean => loadedState.allThirdPartyCosts
)

/** State */
const selectCurrentScope = createSelector(
  getFeatureState,
  (state: ScopeOverviewState): ScopeVersion => plainToInstance(ScopeVersion, state.currentScope)
)
const selectCurrentScopeConfiguration = createSelector(
  selectCurrentScope,
  (currentScope: ScopeVersion): ScopeConfiguration => currentScope?.configuration
)

const selectActivities = createSelector(getFeatureState, (state: ScopeOverviewState): Activities => state.activities)

const selectScopeRetainedHourStats = createSelector(
  getFeatureState,
  (state: ScopeOverviewState): ScopeRetainedHoursStats => state.scopeRetainedHourStats
)

const selectSowRetainedHourStats = createSelector(
  getFeatureState,
  (state: ScopeOverviewState): SowRetainedHoursStats => state.sowRetainedHourStats
)

const selectThirdPartyCostBalance = createSelector(
  getFeatureState,
  (state: ScopeOverviewState): Money => state.thirdPartyCostBalance
)

const selectDeliverableLibraryTemplate = createSelector(
  getFeatureState,
  (state: ScopeOverviewState): any => state.deliverableLibraryTemplate
)

const selectDeliverableLibraryEntry = createSelector(
  getFeatureState,
  (state: ScopeOverviewState): any => state.deliverableLibraryEntry
)

const selectOutputTemplate = createSelector(getFeatureState, (state: ScopeOverviewState): any => state.outputTemplate)

const selectDocxPreview = createSelector(getFeatureState, (state: ScopeOverviewState): any => state.docxPreview)

const selectPreviewError = createSelector(getFeatureState, (state: ScopeOverviewState): any => state.previewError)

const selectSecondPartyForScope = createSelector(getFeatureState, (state): SecondParty => state.secondParty)

const selectOfficeLocationForScope = createSelector(getFeatureState, (state): OfficeLocation => state.officeLocation)

const selectCustomFieldsOutdated = createSelector(getFeatureState, (state): boolean => state.customFieldsOutdated)

const selectDefaultRateCard = createSelector(getFeatureState, (state): RatecardVersion => state.defaultRateCard)

const selectRetainedTeamStats = createSelector(
  getFeatureState,
  (state): SowRetainedTeamStats[] => state.sowRetainedTeamStats
)

const selectAllRateCards = createSelector(
  getFeatureState,
  (state: ScopeOverviewState): RatecardVersion[] => state.rateCards
)

const selectTeamError = createSelector(getFeatureState, (state: ScopeOverviewState): any => state.teamError)

const selectFilteredDeliverables = createSelector(
  getFeatureState,
  (state: ScopeOverviewState): any => state.filteredDeliverables
)

const selectScopeFees = createSelector(getFeatureState, (state: ScopeOverviewState) => state.currentScope?.feeItems)

const getAllFees = (state: ScopeOverviewState): FeeItemInstance[] => {
  if (!state.loaded.allFees) return []

  const scopeFees = state.currentScope?.feeItems.map((feeItem) => ({ ...feeItem, level: { value: 'scope' } }))
  const sectionFees = state.currentScope.sections.flatMap((section) =>
    section.feeItems.map((feeItem) => ({ ...feeItem, level: { value: 'section' } }))
  )
  const deliverableFees = state.currentScope.deliverables.flatMap((deliverable) =>
    deliverable.feeItems.map((feeItem) => ({ ...feeItem, level: { value: 'deliverable' } }))
  )
  const componentFees = state.allComponentFees.map((feeItem) => ({ ...feeItem, level: { value: 'component' } })) || []

  return scopeFees.concat(sectionFees).concat(deliverableFees).concat(componentFees)
}

const selectAllFees = createSelector(getFeatureState, getAllFees)

const getAllThirdPartyCosts = (state: ScopeOverviewState): ThirdPartyCost[] => {
  if (!state.loaded.allThirdPartyCosts) return []
  const deliverableThirdPartyCosts = state.currentScope.deliverables.flatMap(
    (deliverable) => deliverable.thirdPartyCosts
  )

  return deliverableThirdPartyCosts
    .concat(state.allSectionTPCs || [])
    .concat(state.allComponentTPCs || [])
    .concat(state.currentScope.thirdPartyCosts || [])
}
const selectAllThirdPartyCosts = createSelector(getFeatureState, getAllThirdPartyCosts)

const selectSearchedComponentsForScope = createSelector(
  getFeatureState,
  (state: ScopeOverviewState): any => state.scopeComponents
)

const selectSearchedNonDistinctComponentsForScope = createSelector(
  getFeatureState,
  (state: ScopeOverviewState): any => state.nonDistinctComponents
)

const selectSearchedDepartmentsForScope = createSelector(
  getFeatureState,
  (state: ScopeOverviewState): any => state.scopeDepartments
)

const selectSearchedRolesForScope = createSelector(
  getFeatureState,
  (state: ScopeOverviewState): any => state.scopeRoles
)

const selectDialogId = createSelector(getFeatureState, (state: ScopeOverviewState): string => state.dialogId)

const selectScenario = createSelector(getFeatureState, (state: ScopeOverviewState) =>
  plainToInstance(Scenario, state.scenario)
)

const selectConfigurationHasUpdates = createSelector(
  getFeatureState,
  (state: ScopeOverviewState) => state.configurationHasUpdates
)

const selectScopeStatus = createSelector(
  getFeatureState,
  (state: ScopeOverviewState): StatusType => state.currentScope.status
)

const selectAvailableDeliverableTypes = (currencyCode: string) =>
  createSelector(getFeatureState, (state: ScopeOverviewState): any =>
    (state.availableDeliverableTypes || [])
      .filter((d) => !d.isFixedCost || d.complexityPriceL?.currency === currencyCode)
      .sort((a, b) => (a.name.toLowerCase().trim() > b.name.toLowerCase().trim() ? 1 : -1))
  )

const selectComponentsByDeliverable = (deliverableId: number) =>
  createSelector(
    getFeatureState,
    (state): any => state.currentScope.deliverables.find((d) => d.id === deliverableId)?.components
  )

const selectDeliverable = (deliverableId: number) =>
  createSelector(getFeatureState, (state: ScopeOverviewState): any =>
    state.currentScope.deliverables.find((deliverable) => deliverable.id === deliverableId)
  )

const selectDeliverablesPagination = createSelector(
  getFeatureState,
  (state: ScopeOverviewState): ScopePagination<Deliverable> => state.currentScopeDeliverablesPagination
)
const selectIsDeliverablesLoading = createSelector(
  selectDeliverablesPagination,
  (pagination: ScopePagination<Deliverable>): boolean => pagination?.loading
)

const selectSectionsPagination = createSelector(
  getFeatureState,
  (state: ScopeOverviewState): ScopePagination<ScopeSection> => state.currentScopeSectionsPagination
)
const selectIsSectionsLoading = createSelector(
  selectSectionsPagination,
  (pagination: ScopePagination<ScopeSection>): boolean => pagination?.loading
)

const selectSectionDeliverablesPagination = createSelector(
  getFeatureState,
  (state: ScopeOverviewState): KeyValue<ScopePagination<Deliverable>> => state.currentScopeSectionDeliverablesPagination
)
const selectIsSectionDeliverablesLoading = (sectionId: number) =>
  createSelector(
    selectSectionDeliverablesPagination,
    (pagination: KeyValue<ScopePagination<Deliverable>>): boolean => pagination[sectionId]?.loading
  )

const selectSectionPagination = (sectionId) =>
  createSelector(
    getFeatureState,
    (state: ScopeOverviewState): ScopePagination<Deliverable> =>
      state.currentScopeSectionDeliverablesPagination[sectionId]
  )

const selectDisplayedDeliverablesCount = createSelector(
  getFeatureState,
  (state: ScopeOverviewState): number => state.displayedDeliverablesCount
)

const selectDisplayedDeliverables = createSelector(
  selectCurrentScope,
  selectDisplayedDeliverablesCount,
  (scope: ScopeVersion, count: number): Deliverable[] => scope?.deliverables?.slice(0, count) || []
)

const selectBufferedDeliverablesCount = createSelector(
  selectCurrentScope,
  selectDisplayedDeliverablesCount,
  (scope: ScopeVersion, displayedCount: number): number => (scope?.deliverables?.length || 0) - displayedCount
)

export const ScopeOverviewSelectors = {
  selectActivities,
  selectComponentsByDeliverable,
  selectCurrentScope,
  selectCurrentScopeConfiguration,
  selectDeliverablesPagination,
  selectIsDeliverablesLoading,
  selectSectionsPagination,
  selectIsSectionsLoading,
  selectSectionDeliverablesPagination,
  selectIsSectionDeliverablesLoading,
  selectSectionPagination,
  selectDisplayedDeliverables,
  selectBufferedDeliverablesCount,
  selectScopeRetainedHourStats,
  selectSowRetainedHourStats,
  selectThirdPartyCostBalance,
  selectAvailableDeliverableTypes,
  selectDeliverableLibraryTemplate,
  selectDeliverableLibraryEntry,
  selectOutputTemplate,
  selectDocxPreview,
  selectPreviewError,
  selectSecondPartyForScope,
  selectOfficeLocationForScope,
  selectCustomFieldsOutdated,
  selectDefaultRateCard,
  selectRetainedTeamStats,
  selectAllRateCards,
  selectTeamError,
  selectFilteredDeliverables,
  selectScopeFees,
  selectAllFees,
  selectAllThirdPartyCosts,
  selectSearchedComponentsForScope,
  selectSearchedNonDistinctComponentsForScope,
  selectSearchedDepartmentsForScope,
  selectSearchedRolesForScope,
  selectDeliverable,
  selectDialogId,
  selectScenario,
  selectConfigurationHasUpdates,
  selectScopeStatus,

  selectAllFeesLoaded,
  selectCurrentScopeLoaded,
  selectLibraryTemplateLoaded,
  selectUpdateDeliverableLoaded,
  selectAllThirdPartyCostsLoaded,
  selectUpdateScopeDetailsLoaded,
  selectCurrentScopeValuesLoading,
  selectUpdateScopeDetailsLoading,
  selectAllTasksLoading,
  selectCurrentScopeLoading,
  selectAddScopeDeliverableLoading,
  selectAddScopeSectionLoading,
  selectDeliverableLoading,
  selectSectionLoading,
}
